import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Mail, CheckCircle2, Loader2, RefreshCw } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

export default function VerifyEmailPage() {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const [sent, setSent] = useState(false);

    // Get email from localStorage if available
    useEffect(() => {
        const storedUser = localStorage.getItem('pendingUser');
        if (storedUser) {
            try {
                const user = JSON.parse(storedUser);
                setEmail(user.email || '');
            } catch (e) {}
        }
    }, []);

    // Cooldown timer
    useEffect(() => {
        if (cooldown > 0) {
            const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [cooldown]);

    const handleResend = async () => {
        if (!email) {
            toast.error('Veuillez entrer votre adresse email');
            return;
        }
        
        setLoading(true);
        try {
            await api.post('/auth/resend-confirmation', { email });
            toast.success('Email de confirmation envoyé !');
            setSent(true);
            setCooldown(60);
        } catch (error) {
            const detail = error.response?.data?.detail;
            if (error.response?.status === 429) {
                // Rate limited
                const match = detail?.match(/(\d+) secondes/);
                if (match) {
                    setCooldown(parseInt(match[1]));
                }
                toast.error(detail);
            } else if (detail === 'Ce compte est déjà confirmé.') {
                toast.success('Votre compte est déjà confirmé !');
                navigate('/login');
            } else {
                toast.success('Si un compte existe avec cet email, un lien a été envoyé.');
                setSent(true);
                setCooldown(60);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#681793]/5 to-[#FF914D]/5 p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-[#681793]/10 rounded-full flex items-center justify-center">
                        <Mail className="w-8 h-8 text-[#681793]" />
                    </div>
                    <CardTitle>Vérifiez votre boîte mail</CardTitle>
                    <CardDescription>
                        Un email de confirmation a été envoyé à votre adresse.
                        Cliquez sur le lien dans l'email pour activer votre compte.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="p-4 bg-blue-50 rounded-lg text-sm text-blue-700">
                        <p>💡 Le lien expire dans 24 heures.</p>
                        <p className="mt-1">Pensez à vérifier vos spams si vous ne trouvez pas l'email.</p>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="email">Votre email</Label>
                        <Input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="votre@email.com"
                            data-testid="resend-email-input"
                        />
                    </div>

                    <Button
                        onClick={handleResend}
                        disabled={loading || cooldown > 0}
                        className="w-full"
                        variant={sent ? 'outline' : 'default'}
                        data-testid="resend-confirmation-btn"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Envoi en cours...
                            </>
                        ) : cooldown > 0 ? (
                            <>
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Renvoyer dans {cooldown}s
                            </>
                        ) : sent ? (
                            <>
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Renvoyer l'email
                            </>
                        ) : (
                            <>
                                <Mail className="w-4 h-4 mr-2" />
                                Renvoyer l'email de confirmation
                            </>
                        )}
                    </Button>

                    {sent && (
                        <div className="p-4 bg-green-50 rounded-lg flex items-center gap-3">
                            <CheckCircle2 className="w-5 h-5 text-green-500" />
                            <p className="text-sm text-green-700">
                                Un nouvel email a été envoyé. Vérifiez votre boîte de réception.
                            </p>
                        </div>
                    )}

                    <div className="pt-4 border-t text-center">
                        <Link 
                            to="/login" 
                            className="text-sm text-muted-foreground hover:text-foreground"
                        >
                            Retour à la connexion
                        </Link>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
